// SNIGJ - Case Detail Page

import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { t } from '../lib/i18n';
import { casesAPI, documentsAPI } from '../lib/api';
import { DRC_ADMINISTRATIVE_HIERARCHY, JUDICIAL_HIERARCHY } from '../lib/drc-data';
import DashboardLayout from '../components/layout/DashboardLayout';
import AccessDeniedAlert from '../components/AccessDeniedAlert';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Badge } from '../components/ui/badge';
import { 
  ArrowLeft, 
  Save, 
  FileText, 
  Upload,
  Image,
  Video,
  Music,
  File,
  Trash2,
  Download,
  Eye,
  Users,
  MapPin,
  Calendar,
  Shield,
  Send,
  Clock,
  Scale,
  Share2
} from 'lucide-react';
import { toast } from 'sonner';
import api from '../lib/api';
import JurisdictionShareModal from '../components/JurisdictionShareModal';

const CaseDetailPage = () => {
  const { caseId } = useParams();
  const navigate = useNavigate();
  const { language, user } = useAuth();
  const isNew = caseId === 'new';
  
  const [loading, setLoading] = useState(!isNew);
  const [saving, setSaving] = useState(false);
  const [attachments, setAttachments] = useState([]);
  const [caseHistory, setCaseHistory] = useState([]);
  const [accessDenied, setAccessDenied] = useState(null);
  const [showShareModal, setShowShareModal] = useState(false); // {reason, needsRequisition}
  
  // Jurisdiction state
  const [selectedProvince, setSelectedProvince] = useState('');
  const [selectedTerritory, setSelectedTerritory] = useState('');
  const [selectedCommune, setSelectedCommune] = useState('');
  const [selectedQuartier, setSelectedQuartier] = useState('');
  
  const [formData, setFormData] = useState({
    case_number: '',
    case_type: 'CRIMINAL',
    title: '',
    description: '',
    classification: 'PUBLIC',
    status: 'OPEN',
    jurisdiction: {
      province: '',
      territory: '',
      commune: '',
      quartier: '',
      full_address: ''
    },
    territorial_level: 'CITY',
    parties: [],
    charges: [],
    // Assignment
    current_level: 'police_locale',
    assigned_to: '',
    assignment_history: []
  });

  useEffect(() => {
    if (!isNew) {
      loadCase();
    }
  }, [caseId]);

  const loadCase = async () => {
    try {
      setLoading(true);
      const response = await casesAPI.get(caseId);
      const caseData = response.data;
      
      // Handle jurisdiction - it can be string or object
      let jurisdictionData = caseData.jurisdiction;
      if (typeof jurisdictionData === 'string') {
        jurisdictionData = {
          province: jurisdictionData,
          territory: '',
          commune: '',
          quartier: '',
          full_address: ''
        };
      }
      
      setFormData({
        ...caseData,
        jurisdiction: jurisdictionData
      });
      
      // Set jurisdiction selections
      if (jurisdictionData) {
        setSelectedProvince(jurisdictionData.province || '');
        setSelectedTerritory(jurisdictionData.territory || '');
        setSelectedCommune(jurisdictionData.commune || '');
        setSelectedQuartier(jurisdictionData.quartier || '');
      }
      
      // Load attachments
      if (caseData.case_id) {
        try {
          const docsResponse = await documentsAPI.list({ case_id: caseData.case_id });
          setAttachments(docsResponse.data || []);
        } catch (docError) {
          console.error('Failed to load attachments:', docError);
        }
      }
    } catch (error) {
      console.error('Failed to load case:', error);
      
      // Check if it's an access denied error
      if (error.response?.status === 403) {
        const detail = error.response?.data?.detail || '';
        const needsRequisition = detail.includes('réquisition') || detail.includes('Requisition');
        setAccessDenied({
          reason: detail,
          needsRequisition
        });
      } else {
        toast.error(language === 'fr' ? 'Erreur de chargement' : 'Failed to load case');
        navigate('/cases');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const dataToSave = {
        ...formData,
        jurisdiction: {
          province: selectedProvince,
          territory: selectedTerritory,
          commune: selectedCommune,
          quartier: selectedQuartier,
          full_address: formData.jurisdiction?.full_address || ''
        }
      };
      
      if (isNew) {
        await casesAPI.create(dataToSave);
        toast.success(language === 'fr' ? 'Dossier créé' : 'Case created');
      } else {
        await casesAPI.update(caseId, dataToSave);
        toast.success(language === 'fr' ? 'Dossier mis à jour' : 'Case updated');
      }
      navigate('/cases');
    } catch (error) {
      toast.error(error.response?.data?.detail || (language === 'fr' ? 'Erreur de sauvegarde' : 'Save failed'));
    } finally {
      setSaving(false);
    }
  };

  const handleFileUpload = async (e) => {
    const files = Array.from(e.target.files);
    
    for (const file of files) {
      try {
        const formDataUpload = new FormData();
        formDataUpload.append('file', file);
        formDataUpload.append('title', file.name);
        formDataUpload.append('document_type', getDocumentType(file.type));
        formDataUpload.append('classification', formData.classification);
        if (!isNew && caseId) {
          formDataUpload.append('case_id', caseId);
        }
        
        await documentsAPI.upload(formDataUpload);
        toast.success(`${file.name} ${language === 'fr' ? 'téléversé' : 'uploaded'}`);
        
        if (!isNew) {
          const docsResponse = await documentsAPI.list({ case_id: caseId });
          setAttachments(docsResponse.data || []);
        }
      } catch (error) {
        toast.error(`${language === 'fr' ? 'Erreur:' : 'Error:'} ${file.name}`);
      }
    }
  };

  const getDocumentType = (mimeType) => {
    if (mimeType.startsWith('image/')) return 'IMAGE';
    if (mimeType.startsWith('video/')) return 'VIDEO';
    if (mimeType.startsWith('audio/')) return 'AUDIO';
    if (mimeType.includes('pdf')) return 'PDF';
    return 'OTHER';
  };

  const getFileIcon = (type) => {
    switch (type) {
      case 'IMAGE': return Image;
      case 'VIDEO': return Video;
      case 'AUDIO': return Music;
      case 'PDF': return FileText;
      default: return File;
    }
  };

  // Get available territories based on selected province
  const getAvailableTerritories = () => {
    const province = DRC_ADMINISTRATIVE_HIERARCHY.provinces.find(p => p.id === selectedProvince);
    return province?.territoires || province?.communes || [];
  };

  // Get available communes
  const getAvailableCommunes = () => {
    const province = DRC_ADMINISTRATIVE_HIERARCHY.provinces.find(p => p.id === selectedProvince);
    if (province?.type === 'ville-province') {
      return province.communes || [];
    }
    const territory = province?.territoires?.find(t => t.id === selectedTerritory);
    return territory?.communes || [];
  };

  // Get available quartiers
  const getAvailableQuartiers = () => {
    const province = DRC_ADMINISTRATIVE_HIERARCHY.provinces.find(p => p.id === selectedProvince);
    if (province?.type === 'ville-province') {
      const commune = province.communes?.find(c => c.id === selectedCommune);
      return commune?.quartiers || [];
    }
    return [];
  };

  const caseTypes = ['CRIMINAL', 'CIVIL', 'ADMINISTRATIVE'];
  const statuses = ['OPEN', 'IN_PROGRESS', 'PENDING', 'CLOSED', 'ARCHIVED'];
  const classifications = ['PUBLIC', 'RESTRICTED', 'CONFIDENTIAL', 'SECRET', 'TOP_SECRET'];

  if (loading) {
    return (
      <DashboardLayout>
        <div className="space-y-4">
          <div className="h-8 w-64 skeleton" />
          <div className="h-96 skeleton" />
        </div>
      </DashboardLayout>
    );
  }

  // Show access denied screen if applicable
  if (accessDenied) {
    return (
      <DashboardLayout>
        <div className="max-w-2xl mx-auto py-8">
          <Button variant="ghost" className="mb-4" onClick={() => navigate('/cases')}>
            <ArrowLeft className="w-4 h-4 mr-2" />
            {language === 'fr' ? 'Retour aux dossiers' : 'Back to cases'}
          </Button>
          <AccessDeniedAlert
            reason={accessDenied.reason}
            needsRequisition={accessDenied.needsRequisition}
            resourceType="CASE"
            resourceId={caseId}
            resourceTitle={`Dossier ${caseId}`}
          />
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="space-y-6" data-testid="case-detail-page">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button variant="ghost" size="icon" onClick={() => navigate('/cases')}>
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <div>
              <h1 className="font-heading text-2xl font-bold">
                {isNew 
                  ? (language === 'fr' ? 'Nouveau Dossier' : 'New Case')
                  : formData.case_number || formData.title
                }
              </h1>
              {!isNew && (
                <div className="flex items-center gap-2 mt-1">
                  <Badge className={`classification-${formData.classification?.toLowerCase()}`}>
                    {t(language, `classifications.${formData.classification}`)}
                  </Badge>
                  <Badge variant="outline">{formData.status}</Badge>
                </div>
              )}
            </div>
          </div>
          <div className="flex gap-2">
            {!isNew && (
              <Button variant="outline" className="gap-2" onClick={() => setShowShareModal(true)} data-testid="share-case-btn">
                <Share2 className="w-4 h-4" />
                {language === 'fr' ? 'Partager' : 'Share'}
              </Button>
            )}
            {!isNew && (
              <Link to={`/cases/${caseId}/trial`}>
                <Button variant="outline" className="gap-2">
                  <Scale className="w-4 h-4" />
                  {language === 'fr' ? 'Suivi du procès' : 'Trial Tracking'}
                </Button>
              </Link>
            )}
            <Button onClick={handleSave} disabled={saving} className="gap-2">
              <Save className="w-4 h-4" />
              {saving ? (language === 'fr' ? 'Sauvegarde...' : 'Saving...') : t(language, 'common.save')}
            </Button>
          </div>
        </div>

        <Tabs defaultValue="info" className="space-y-4">
          <TabsList>
            <TabsTrigger value="info">{language === 'fr' ? 'Informations' : 'Information'}</TabsTrigger>
            <TabsTrigger value="jurisdiction">{language === 'fr' ? 'Juridiction' : 'Jurisdiction'}</TabsTrigger>
            <TabsTrigger value="attachments">{language === 'fr' ? 'Pièces jointes' : 'Attachments'}</TabsTrigger>
            <TabsTrigger value="assignment">{language === 'fr' ? 'Attribution' : 'Assignment'}</TabsTrigger>
            {!isNew && <TabsTrigger value="history">{language === 'fr' ? 'Historique' : 'History'}</TabsTrigger>}
          </TabsList>

          {/* Information Tab */}
          <TabsContent value="info">
            <Card>
              <CardHeader>
                <CardTitle>{language === 'fr' ? 'Informations du dossier' : 'Case Information'}</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>{t(language, 'cases.caseNumber')}</Label>
                    <Input
                      value={formData.case_number}
                      onChange={(e) => setFormData({ ...formData, case_number: e.target.value })}
                      placeholder="RP/2024/001"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>{t(language, 'cases.caseType')}</Label>
                    <Select
                      value={formData.case_type}
                      onValueChange={(value) => setFormData({ ...formData, case_type: value })}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {caseTypes.map((type) => (
                          <SelectItem key={type} value={type}>
                            {t(language, `cases.${type.toLowerCase()}`)}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <div className="space-y-2">
                  <Label>{language === 'fr' ? 'Titre' : 'Title'}</Label>
                  <Input
                    value={formData.title}
                    onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                  />
                </div>

                <div className="space-y-2">
                  <Label>{t(language, 'cases.description')}</Label>
                  <Textarea
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    rows={4}
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>{t(language, 'cases.classification')}</Label>
                    <Select
                      value={formData.classification}
                      onValueChange={(value) => setFormData({ ...formData, classification: value })}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {classifications.map((c) => (
                          <SelectItem key={c} value={c}>
                            {t(language, `classifications.${c}`)}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label>{t(language, 'cases.status')}</Label>
                    <Select
                      value={formData.status}
                      onValueChange={(value) => setFormData({ ...formData, status: value })}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {statuses.map((s) => (
                          <SelectItem key={s} value={s}>{s}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <div className="space-y-2">
                  <Label>{language === 'fr' ? 'Chefs d\'accusation' : 'Charges'}</Label>
                  <Textarea
                    value={formData.charges?.join('\n') || ''}
                    onChange={(e) => setFormData({ ...formData, charges: e.target.value.split('\n').filter(c => c.trim()) })}
                    placeholder={language === 'fr' ? 'Un chef d\'accusation par ligne' : 'One charge per line'}
                    rows={3}
                  />
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Jurisdiction Tab */}
          <TabsContent value="jurisdiction">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <MapPin className="w-5 h-5" />
                  {language === 'fr' ? 'Localisation juridictionnelle' : 'Jurisdictional Location'}
                </CardTitle>
                <CardDescription>
                  {language === 'fr' 
                    ? 'Sélectionnez la localisation administrative du dossier'
                    : 'Select the administrative location of the case'}
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>{language === 'fr' ? 'Province' : 'Province'}</Label>
                    <Select
                      value={selectedProvince}
                      onValueChange={(value) => {
                        setSelectedProvince(value);
                        setSelectedTerritory('');
                        setSelectedCommune('');
                        setSelectedQuartier('');
                      }}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder={language === 'fr' ? 'Sélectionner...' : 'Select...'} />
                      </SelectTrigger>
                      <SelectContent>
                        {DRC_ADMINISTRATIVE_HIERARCHY.provinces.map((p) => (
                          <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  {selectedProvince && getAvailableTerritories().length > 0 && (
                    <div className="space-y-2">
                      <Label>{language === 'fr' ? 'Territoire / Ville' : 'Territory / City'}</Label>
                      <Select
                        value={selectedTerritory}
                        onValueChange={(value) => {
                          setSelectedTerritory(value);
                          setSelectedCommune('');
                          setSelectedQuartier('');
                        }}
                      >
                        <SelectTrigger>
                          <SelectValue placeholder={language === 'fr' ? 'Sélectionner...' : 'Select...'} />
                        </SelectTrigger>
                        <SelectContent>
                          {getAvailableTerritories().map((t) => (
                            <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  )}
                </div>

                <div className="grid grid-cols-2 gap-4">
                  {(selectedTerritory || (selectedProvince && DRC_ADMINISTRATIVE_HIERARCHY.provinces.find(p => p.id === selectedProvince)?.type === 'ville-province')) && getAvailableCommunes().length > 0 && (
                    <div className="space-y-2">
                      <Label>{language === 'fr' ? 'Commune' : 'Commune'}</Label>
                      <Select
                        value={selectedCommune}
                        onValueChange={(value) => {
                          setSelectedCommune(value);
                          setSelectedQuartier('');
                        }}
                      >
                        <SelectTrigger>
                          <SelectValue placeholder={language === 'fr' ? 'Sélectionner...' : 'Select...'} />
                        </SelectTrigger>
                        <SelectContent>
                          {getAvailableCommunes().map((c) => (
                            <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  )}

                  {selectedCommune && getAvailableQuartiers().length > 0 && (
                    <div className="space-y-2">
                      <Label>{language === 'fr' ? 'Quartier' : 'Quarter'}</Label>
                      <Select
                        value={selectedQuartier}
                        onValueChange={setSelectedQuartier}
                      >
                        <SelectTrigger>
                          <SelectValue placeholder={language === 'fr' ? 'Sélectionner...' : 'Select...'} />
                        </SelectTrigger>
                        <SelectContent>
                          {getAvailableQuartiers().map((q) => (
                            <SelectItem key={q} value={q}>{q}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  )}
                </div>

                <div className="space-y-2">
                  <Label>{language === 'fr' ? 'Adresse complète' : 'Full Address'}</Label>
                  <Textarea
                    value={formData.jurisdiction?.full_address || ''}
                    onChange={(e) => setFormData({ 
                      ...formData, 
                      jurisdiction: { ...formData.jurisdiction, full_address: e.target.value }
                    })}
                    placeholder={language === 'fr' ? 'Avenue, numéro, référence...' : 'Avenue, number, reference...'}
                    rows={2}
                  />
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Attachments Tab */}
          <TabsContent value="attachments">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center justify-between">
                  <span className="flex items-center gap-2">
                    <File className="w-5 h-5" />
                    {language === 'fr' ? 'Pièces jointes' : 'Attachments'}
                  </span>
                  <div>
                    <input
                      type="file"
                      id="file-upload"
                      multiple
                      accept="image/*,video/*,audio/*,.pdf,.doc,.docx,.xls,.xlsx"
                      onChange={handleFileUpload}
                      className="hidden"
                    />
                    <Button asChild variant="outline" className="gap-2">
                      <label htmlFor="file-upload" className="cursor-pointer">
                        <Upload className="w-4 h-4" />
                        {language === 'fr' ? 'Ajouter fichier' : 'Add file'}
                      </label>
                    </Button>
                  </div>
                </CardTitle>
                <CardDescription>
                  {language === 'fr' 
                    ? 'Images, vidéos, audios, documents PDF et autres fichiers'
                    : 'Images, videos, audios, PDF documents and other files'}
                </CardDescription>
              </CardHeader>
              <CardContent>
                {attachments.length === 0 ? (
                  <div className="text-center py-12 border-2 border-dashed rounded">
                    <File className="w-12 h-12 mx-auto text-muted-foreground mb-4" />
                    <p className="text-muted-foreground">
                      {language === 'fr' ? 'Aucune pièce jointe' : 'No attachments'}
                    </p>
                    <Button asChild variant="outline" className="mt-4 gap-2">
                      <label htmlFor="file-upload" className="cursor-pointer">
                        <Upload className="w-4 h-4" />
                        {language === 'fr' ? 'Téléverser des fichiers' : 'Upload files'}
                      </label>
                    </Button>
                  </div>
                ) : (
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    {attachments.map((doc) => {
                      const FileIcon = getFileIcon(doc.document_type);
                      return (
                        <div key={doc.document_id} className="border rounded p-3 hover:bg-muted/50">
                          <div className="aspect-square bg-muted rounded flex items-center justify-center mb-2">
                            <FileIcon className="w-12 h-12 text-muted-foreground" />
                          </div>
                          <p className="text-sm font-medium truncate">{doc.title}</p>
                          <p className="text-xs text-muted-foreground">{doc.document_type}</p>
                          <div className="flex gap-1 mt-2">
                            <Button variant="ghost" size="icon" className="h-7 w-7">
                              <Eye className="w-3 h-3" />
                            </Button>
                            <Button variant="ghost" size="icon" className="h-7 w-7">
                              <Download className="w-3 h-3" />
                            </Button>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Assignment Tab */}
          <TabsContent value="assignment">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Shield className="w-5 h-5" />
                  {language === 'fr' ? 'Attribution du dossier' : 'Case Assignment'}
                </CardTitle>
                <CardDescription>
                  {language === 'fr' 
                    ? 'Niveau judiciaire et responsable du dossier'
                    : 'Judicial level and case handler'}
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-2">
                  <Label>{language === 'fr' ? 'Niveau judiciaire actuel' : 'Current Judicial Level'}</Label>
                  <Select
                    value={formData.current_level || 'police_locale'}
                    onValueChange={(value) => setFormData({ ...formData, current_level: value })}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {JUDICIAL_HIERARCHY.map((level) => (
                        <SelectItem key={level.id} value={level.id}>
                          {level.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label>{language === 'fr' ? 'Responsable assigné' : 'Assigned Handler'}</Label>
                  <Input
                    value={formData.assigned_to || ''}
                    onChange={(e) => setFormData({ ...formData, assigned_to: e.target.value })}
                    placeholder={language === 'fr' ? 'ID ou nom du responsable' : 'Handler ID or name'}
                  />
                </div>

                <div className="p-4 bg-yellow-50 dark:bg-yellow-900/20 rounded border border-yellow-200 dark:border-yellow-800">
                  <h4 className="font-medium text-yellow-800 dark:text-yellow-300 mb-2">
                    {language === 'fr' ? 'Note importante' : 'Important Note'}
                  </h4>
                  <p className="text-sm text-yellow-700 dark:text-yellow-400">
                    {language === 'fr' 
                      ? 'Lorsqu\'un dossier est transféré à un niveau supérieur, les utilisateurs du niveau précédent perdent automatiquement l\'accès au dossier.'
                      : 'When a case is transferred to a higher level, users from the previous level automatically lose access to the case.'}
                  </p>
                </div>

                {!isNew && (
                  <Button variant="outline" className="gap-2">
                    <Send className="w-4 h-4" />
                    {language === 'fr' ? 'Transférer le dossier' : 'Transfer Case'}
                  </Button>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* History Tab */}
          {!isNew && (
            <TabsContent value="history">
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Clock className="w-5 h-5" />
                    {language === 'fr' ? 'Historique du dossier' : 'Case History'}
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    <div className="flex items-center gap-4 p-3 border rounded">
                      <div className="w-10 h-10 rounded-full bg-green-100 flex items-center justify-center">
                        <FileText className="w-5 h-5 text-green-600" />
                      </div>
                      <div>
                        <p className="font-medium">{language === 'fr' ? 'Dossier créé' : 'Case created'}</p>
                        <p className="text-sm text-muted-foreground">{formData.created_at}</p>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </TabsContent>
          )}
        </Tabs>
      </div>

      {/* Jurisdiction Share Modal */}
      <JurisdictionShareModal
        isOpen={showShareModal}
        onClose={() => setShowShareModal(false)}
        resourceType="CASE"
        resourceId={caseId}
        resourceTitle={formData.case_number || formData.title || caseId}
      />
    </DashboardLayout>
  );
};

export default CaseDetailPage;
